import { useNavigate, useParams } from 'react-router-dom';
import { ErrorBanner, ItemsSkeleton, useToast } from '../components';
import { useInvitePreview, useRedeemInvite } from '../api/queries';
import { ApiError } from '../api/client';
import { useTelegramContext } from '../hooks/useTelegramContext';

/**
 * Web redeem surface, reached at `/redeem/:token`. Telegram users redeem via
 * the bot's `?start=inv_<token>` deep link instead; this exists so an invite
 * pasted into a browser still lands somewhere useful.
 *
 * It sits *outside* `RequireAuth` on purpose — redeeming is the one path open
 * to a user the bot has never seen, so the invite has to be previewable before
 * sign-in, and only accepting it needs an identity.
 */
export function RedeemScreen() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const ctx = useTelegramContext();
  const { data: invite, isPending, isError, error, refetch } = useInvitePreview(token ?? null);
  const redeem = useRedeemInvite();

  // `useInvitePreview` is disabled without a token, and a disabled query sits
  // in `pending` forever — which would render as a skeleton that never
  // resolves. Handle the missing token before trusting `isPending`.
  if (!token) {
    return (
      <div className="centered-screen">
        <div className="redeem__mark">✦</div>
        <div className="redeem__inviter">This invitation link is incomplete.</div>
        <div className="redeem__actions">
          <button type="button" className="redeem__decline" onClick={() => navigate('/')}>
            Go to my lists
          </button>
        </div>
      </div>
    );
  }

  if (isPending) return <ItemsSkeleton />;

  if (isError) {
    // Invites are single-use, so 404 is the *expected* failure here, not a
    // server problem — there is nothing to retry.
    const spent = error instanceof ApiError && error.isMissing;
    return (
      <div className="app">
        {spent ? null : <ErrorBanner message={error.message} onRetry={() => void refetch()} />}
        <div className="centered-screen">
          <div className="redeem__mark">✦</div>
          <div className="redeem__inviter">
            {spent
              ? 'This invitation is invalid or has already been used. Ask for a fresh one.'
              : 'This invitation could not be loaded.'}
          </div>
          <div className="redeem__actions">
            <button type="button" className="redeem__decline" onClick={() => navigate('/')}>
              Go to my lists
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="centered-screen">
      <div className="redeem__mark">✦</div>
      <div className="redeem__inviter">
        {invite?.inviterName ? `${invite.inviterName} invited you to` : 'You were invited to'}
      </div>
      <div className="redeem__list">“{invite?.listName}”</div>
      <div style={{ marginTop: 8 }}>
        <span className="chip chip--role">as {invite?.role}</span>
      </div>
      <div className="redeem__actions">
        <button
          type="button"
          className="redeem__accept"
          disabled={redeem.isPending}
          onClick={() => {
            if (ctx.mode === 'unauthenticated') {
              navigate(`/auth?next=${encodeURIComponent(`/redeem/${token}`)}`);
              return;
            }
            redeem.mutate(token, {
              onSuccess: (list) => {
                showToast(`Joined “${list.name}”`);
                navigate(`/lists/${list.id}`, { replace: true });
              },
              onError: (err) =>
                showToast(
                  err instanceof ApiError && err.isMissing
                    ? 'That invite has already been used'
                    : "That invite couldn't be redeemed",
                ),
            });
          }}
        >
          {redeem.isPending ? 'Joining…' : 'Accept invitation'}
        </button>
        <button type="button" className="redeem__decline" onClick={() => navigate('/')}>
          Decline
        </button>
      </div>
    </div>
  );
}
